import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Building blocks the menu hub's registry tabs share (DESIGN.md §5–6).
 */

/** Confirmation that names the object and the consequence (DESIGN.md §5.4 rule 6). */
export function ConfirmDialog({ open, title, body, confirmLabel, onConfirm, onCancel, testId, tone = "destructive" }) {
  return (
    <AlertDialog open={open} onOpenChange={next => { if (!next) onCancel(); }}>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={testId && `${testId}-cancel`}>Keep it</AlertDialogCancel>
          {/* Every caller deactivates, hides or deletes: red, per DESIGN.md §6.4. */}
          <AlertDialogAction variant={tone} onClick={onConfirm} data-testid={testId && `${testId}-confirm`}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * A name that edits in place: Enter or leaving the field saves, Esc puts it back.
 * `onCommit` answers `{ ok }`; on failure the old name returns, so the cell never
 * shows a name the server refused.
 */
export function InlineName({ value, onCommit, disabled, testId, label }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === value) { setDraft(value); return; }
    const result = await onCommit(next);
    if (!result?.ok) setDraft(value);
  };

  return (
    <input
      className="h-8 w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-body hover:border-input focus:border-primary focus:outline-none disabled:hover:border-transparent"
      value={draft}
      disabled={disabled}
      aria-label={label}
      onChange={event => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={event => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") { setDraft(value); event.currentTarget.blur(); }
      }}
      data-testid={testId}
    />
  );
}

/** Up / down arrows that move a row one place. */
export function MoveButtons({ index, count, onMove, disabled, testIdPrefix }) {
  const cls = "inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent";
  return (
    <span className="inline-flex">
      <button type="button" className={cls} disabled={disabled || index === 0} onClick={() => onMove(index, -1)} aria-label="Move up" data-testid={`${testIdPrefix}-up`}>
        <ArrowUp className="h-4 w-4" />
      </button>
      <button type="button" className={cls} disabled={disabled || index === count - 1} onClick={() => onMove(index, 1)} aria-label="Move down" data-testid={`${testIdPrefix}-down`}>
        <ArrowDown className="h-4 w-4" />
      </button>
    </span>
  );
}

/**
 * The writes a one-place move needs. Imported data often carries the same
 * `sort_order` on every row, where swapping two values changes nothing — so the
 * list is renumbered in steps of 10 and only rows whose number changes are sent.
 */
export function reorderPatches(rows, index, delta) {
  const target = index + delta;
  if (target < 0 || target >= rows.length) return [];
  const order = [...rows];
  [order[index], order[target]] = [order[target], order[index]];
  return order
    .map((row, position) => ({ id: row.id, sort_order: (position + 1) * 10, was: row.sort_order }))
    .filter(entry => entry.sort_order !== entry.was)
    .map(({ id, sort_order }) => ({ id, sort_order }));
}

/** The chevron that opens a group or master-group row onto what is inside it. */
export function OpenButton({ open, onClick, label, testId }) {
  const Twisty = open ? ChevronDown : ChevronRight;
  return (
    <button type="button" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onClick} aria-expanded={open} aria-label={label} data-testid={testId}>
      <Twisty className="h-4 w-4" />
    </button>
  );
}

/** "Paneer Tikka, Chicken 65 +12 more" — a category's items in one line. */
export const itemNames = category => {
  const names = (category.items || []).map(item => item.name);
  return names.length > 6 ? `${names.slice(0, 6).join(", ")} +${names.length - 6} more` : names.join(", ");
};

/** Table header cell in the desktop table voice (DESIGN.md §5.6). */
export const TH = "px-3 h-9 text-meta font-medium text-muted-foreground text-left";
export const TD = "px-3 py-1 text-body align-middle";
